import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineCommentEditIcon from 'LuxKit/Icons/line/LineCommentEditIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Label from 'LuxKit/Label/Label'
import Group from 'components/utils/Group'
import ContactOptions from 'components/Common/ContactOptions'
import {
  TripPlannerStickyHeader,
  TripPlannerStyledLogo,
  LeftContent,
  RightContent,
} from 'tripPlanner/components/Header/Shared'
import type { TripPlannerChat } from 'tripPlanner/api/types'
import type { BasicTrip } from 'tripPlanner/types/common'
import TripPlannerDrawer from './TripPlannerDrawer'
import { type PromptOption } from './PromptSelector'
import Clickable from 'components/Common/Clickable'
import NotificationBanner from 'components/App/Header/HeaderBanners/NotificationBanner'

const TertiaryHeader = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.seven};
  padding: ${rem(4)} ${rem(16)};
`

const LogoWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`

const StyledStickyHeader = styled(TripPlannerStickyHeader)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
`

interface Props {
  isLoggedIn: boolean
  recentChats?: Array<TripPlannerChat>
  recentTrips?: Array<BasicTrip>
  onStartNewChat: () => void
  onChatSelect?: (chatId: number) => void
  onChatDelete?: (chatId: number) => void
  onTripDelete?: (tripId: string) => void
  className?: string
  isStaff?: boolean
  isDevMode?: boolean
  onDevModeToggle?: () => void
  canManagePrompts?: boolean
  prompts?: Array<PromptOption>
  selectedPromptKey?: string
  onPromptSelect?: (key: string) => void
  onPromptAdd?: () => void
}

function TripPlannerHeader({
  isLoggedIn,
  recentChats,
  recentTrips,
  onStartNewChat,
  onChatSelect,
  onChatDelete,
  onTripDelete,
  className,
  isStaff,
  isDevMode,
  onDevModeToggle,
  canManagePrompts,
  prompts,
  selectedPromptKey,
  onPromptSelect,
  onPromptAdd,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const handleOpenDrawer = useCallback(() => {
    showModal(
      <TripPlannerDrawer
        onStartNewChat={onStartNewChat}
        recentChats={recentChats}
        recentTrips={recentTrips}
        onChatSelect={onChatSelect}
        onChatDelete={onChatDelete}
        onTripDelete={onTripDelete}
        isStaff={isStaff}
        isDevMode={isDevMode}
        onDevModeToggle={onDevModeToggle}
        canManagePrompts={canManagePrompts}
        prompts={prompts}
        selectedPromptKey={selectedPromptKey}
        onPromptSelect={onPromptSelect}
        onPromptAdd={onPromptAdd}
      />,
    )
  }, [showModal, onStartNewChat, recentChats, recentTrips, onChatSelect, onChatDelete, onTripDelete, isStaff, isDevMode, onDevModeToggle, canManagePrompts, prompts, selectedPromptKey, onPromptSelect, onPromptAdd])

  const handleLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const feedbackBanner: App.NotificationBanner = {
    bannerText: intl.formatMessage({
      id: 'amW3oJYAsXdpc2bjStOU',
      description: 'Feedback banner text informing users they have been selected to try AI Trip Planner',
      defaultMessage: 'Hi there! You’ve been selected to try our new chat-based AI Trip Planner.',
    }),
    linkText: intl.formatMessage({
      id: 'M8BRqMk0oEdL7TSRoyxS',
      defaultMessage: 'Share your feedback',
      description: 'Link text in the feedback banner to open the TripPlanner feedback survey form',
    }),
    linkTo: 'https://usabi.li/do/gizugyzacokd/vj9hxb',
    openInNewTab: true,
    id: 'trip-planner-feedback-banner',
    slug: 'trip-planner-feedback-banner',
    show: true,
  }

  const loginButtonText = intl.formatMessage({
    id: 'ARcYTJ2nmHAv35KNSneu',
    defaultMessage: 'Log in to save your trip',
    description: 'Button text prompting unauthenticated users to log in to save their trip plan',
  })

  const backButtonText = intl.formatMessage({
    id: 'uvkQRhGsEtSZkBcb2NHS',
    defaultMessage: 'Back to Luxury Escapes',
    description: 'Navigation button text to return to the main Luxury Escapes site from TripPlanner',
  })

  const giveFeedbackText = intl.formatMessage({
    id: 'DwyX2RGwDgu5GbANxFYx',
    defaultMessage: 'Give feedback',
    description: 'Button text in the TripPlanner header to open the feedback survey for the feature',
  })

  const openMenuAriaLabel = intl.formatMessage({
    id: 'sIGePGauk8KDHGHEBhex',
    defaultMessage: 'Open menu',
    description: 'Accessible label for the hamburger menu button in the TripPlanner header bar',
  })

  const aiTripPlannerLabel = intl.formatMessage({
    id: 'EYQsWpC_SwW6ZS0Xb0Yz',
    defaultMessage: 'AI TripPlanner',
    description: 'Branding label for the AI TripPlanner feature in the header',
  })
  const betaLabel = intl.formatMessage({
    id: 'gO283xrN3xwQSsK25Jy9',
    defaultMessage: 'Beta',
    description: 'Beta badge label for AI TripPlanner in the header in the trip planner AI chat',
  })

  const rightContent = useMemo(() => {
    if (isLoggedIn) {
      return undefined
    }

    return (
      <TextButton
        kind="secondary"
        size="medium"
        onClick={handleLogin}
      >
        {loginButtonText}
      </TextButton>
    )
  }, [isLoggedIn, handleLogin, loginButtonText])

  return (
    <Group direction="vertical" className={className}>
      <TertiaryHeader
        direction="horizontal"
        horizontalAlign="space-between"
        verticalAlign="center"
        fullWidth
      >
        <TextButton
          kind="tertiary"
          size="small"
          startIcon={<LineArrowLeftIcon />}
          to="/"
        >
          {backButtonText}
        </TextButton>
        <Group direction="horizontal" gap={16} verticalAlign="center">
          <ContactOptions />
          <TextButton
            kind="tertiary"
            size="small"
            startIcon={<LineCommentEditIcon />}
            href="https://usabi.li/do/gizugyzacokd/vj9hxb"
            target="_blank"
            rel="noopener noreferrer"
          >
            {giveFeedbackText}
          </TextButton>
        </Group>
      </TertiaryHeader>
      <NotificationBanner overrideBanner={feedbackBanner} />
      <StyledStickyHeader>
        <LeftContent>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <IconButton
              kind="tertiary"
              size="medium"
              onClick={handleOpenDrawer}
              aria-label={openMenuAriaLabel}
            >
              <LineBarsIcon />
            </IconButton>
            <Clickable to="/trip-planner/chat-trip-planner/new">
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <LogoWrapper>
                  <TripPlannerStyledLogo />
                </LogoWrapper>
                <BodyText variant="large" weight="bold">{aiTripPlannerLabel}</BodyText>
                <Label kind="primary" variant="highlight">{betaLabel}</Label>
              </Group>
            </Clickable>
          </Group>
        </LeftContent>
        {rightContent && (
          <RightContent>
            {rightContent}
          </RightContent>
        )}
      </StyledStickyHeader>
    </Group>
  )
}

export default TripPlannerHeader
